import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MessageCircle, Phone } from 'lucide-react';
import BlurFade from '../components/primitives/BlurFade';
import Seo from '../components/Seo';
import Configurator from '../components/Configurator';
import { useLang } from '../LangContext';
import { company } from '../i18n';

const telHref = (p) => `tel:${(p || '').replace(/\s/g, '')}`;
const waHref = (msg) => `https://wa.me/${company.whatsapp}?text=${encodeURIComponent(msg || '')}`;

const CONFIGURATOR_URL = process.env.REACT_APP_CONFIGURATOR_URL;

export default function ConfiguratorPage() {
  const { lang } = useLang();
  const phone = lang === 'de' ? company.phoneDE : company.phoneNL;

  const labels = lang === 'de' ? {
    eyebrow: 'Konfigurator',
    title: 'Konfiguriere deinen Insektenschutz in 3 Minuten.',
    sub: 'Wähle Produkttyp, Maße, Farbe und Gewebe — wir senden dir ein passgenaues Angebot zurück.',
    wa: 'WhatsApp',
    call: 'Anrufen',
    offerte: 'Angebot anfragen',
    helpTitle: 'Lieber mit uns sprechen?',
    helpSub: 'Wir konfigurieren in wenigen Minuten gemeinsam mit dir.',
  } : {
    eyebrow: 'Configurator',
    title: 'Stel je horren samen in 3 minuten.',
    sub: 'Kies producttype, maten, kleur en gaas — wij sturen u een passende offerte terug.',
    wa: 'WhatsApp',
    call: 'Bel ons',
    offerte: 'Offerte aanvragen',
    helpTitle: 'Liever even bellen?',
    helpSub: 'We configureren samen met je in een paar minuten.',
  };

  return (
    <article data-testid="configurator-page" className="relative bg-[#FAF8F2]">
      <Seo title={labels.title} description={labels.sub} path="/configurator" lang={lang} />
      <section className="pt-28 sm:pt-36 pb-10 lg:pb-14">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10">
          <BlurFade>
            <p className="text-[11.5px] uppercase tracking-[0.22em] text-[#F26B1A] font-medium mb-6">{labels.eyebrow}</p>
          </BlurFade>
          <BlurFade delay={0.05}>
            <h1 className="font-display text-[#1F1D1A] text-[40px] sm:text-[56px] lg:text-[80px] leading-[1.02] max-w-4xl">{labels.title}</h1>
          </BlurFade>
          <BlurFade delay={0.15}>
            <p className="mt-7 text-[#5C5852] text-[16px] lg:text-[18px] leading-relaxed max-w-2xl">{labels.sub}</p>
          </BlurFade>
        </div>
      </section>

      {/* The configurator itself */}
      <section className="bg-[#F2EEDF] py-10 lg:py-16">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10">
          {CONFIGURATOR_URL ? (
            <div className="relative w-full rounded-[24px] overflow-hidden depth-card bg-white" style={{ height: 'min(80vh, 900px)' }}>
              <iframe
                title="Horrenmaker Configurator"
                src={CONFIGURATOR_URL}
                className="absolute inset-0 w-full h-full"
                allow="clipboard-write"
              />
            </div>
          ) : (
            <Configurator />
          )}
        </div>
      </section>

      {/* Side-help block — visible regardless of configurator mode */}
      <section className="py-14 lg:py-20">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 text-center">
          <h2 className="font-display text-[26px] lg:text-[34px] text-[#1F1D1A] leading-tight">{labels.helpTitle}</h2>
          <p className="mt-3 text-[#5C5852] text-[15px]">{labels.helpSub}</p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
            <a href={waHref(lang === 'de' ? 'Hallo, ich brauche Hilfe beim Konfigurieren.' : 'Hallo, ik wil graag samen configureren.')}
              target="_blank" rel="noreferrer" className="pill pill-leaf" data-testid="cfg-help-wa">
              <MessageCircle className="w-4 h-4" /> {labels.wa}
            </a>
            <a href={telHref(phone)} className="pill pill-outline" data-testid="cfg-help-phone">
              <Phone className="w-4 h-4" /> {labels.call} {phone}
            </a>
            <Link to="/offerte" className="pill pill-outline" data-testid="cfg-help-offerte">
              {labels.offerte} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </article>
  );
}
